import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Film, Clock, AlertTriangle, Loader2, ExternalLink, Play, Star, Send, Check, Globe } from 'lucide-react';
import { API_URL } from '../utils/api';
import { useTitreOnglet } from '../hooks/useTitreOnglet';
import axios from 'axios';

const LANG_LABELS = {
  fr: 'Français', en: 'English', ar: 'العربية', pt: 'Portugues',
  es: 'Espanol', de: 'Deutsch', it: 'Italiano', tr: 'Turkce',
  zh: '中文', ru: 'Русский', nl: 'Nederlands', pl: 'Polski',
};

function isYouTubeUrl(url) {
  return url && (url.includes('youtube.com/watch') || url.includes('youtu.be/'));
}
function getYouTubeEmbedUrl(url) {
  let vid = '';
  const m1 = url.match(/v=([a-zA-Z0-9_-]+)/);
  if (m1) vid = m1[1];
  const m2 = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (m2) vid = m2[1];
  // youtube-nocookie : pas de cookie avant lecture, suggestions limitées à la
  // chaîne — le patient reste dans un cadre soigné, pas dans l'algorithme YouTube.
  return vid ? `https://www.youtube-nocookie.com/embed/${vid}?rel=0&modestbranding=1` : null;
}
function isUploadedVideo(url) {
  return url && url.includes('/api/uploads/videos/');
}

function StarRating({ rating, onRate, disabled }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          onClick={() => !disabled && onRate(i)}
          onMouseEnter={() => !disabled && setHover(i)}
          onMouseLeave={() => setHover(0)}
          disabled={disabled}
          className={`transition-transform ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          data-testid={`star-${i}`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              (hover || rating) >= i
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function SharedVideoPage() {
  useTitreOnglet('Vidéo d’accompagnement');
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [watched, setWatched] = useState(() => new Set([0]));

  const fetchVideo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/videos/view/${token}`);
      setData(res.data);
      setCurrentVideoUrl(res.data.video_url);
      if (res.data.rating) {
        setRating(res.data.rating);
        setRatingSubmitted(true);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) setError('expired');
      else if (status === 404) setError('not_found');
      else setError('unknown');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  const handleSubmitRating = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/videos/view/${token}/rate`, {
        rating,
        comment: comment.trim(),
      });
      setRatingSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) setRatingSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const switchLanguage = (alt) => {
    setCurrentVideoUrl(alt.url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-3xl shadow-lg p-8 border border-slate-200/60">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Lien expiré</h1>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              Ce lien de partage vidéo a expiré (valide 24 heures).
              Contactez votre médecin pour recevoir un nouveau lien.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-3xl shadow-lg p-8 border border-slate-200/60">
            <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2 font-['Outfit']">Lien introuvable</h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              Ce lien de partage n'existe pas ou a été supprimé.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // « Ordonnance vidéo » : 1..n vidéos ; les anciens partages arrivent en liste de 1.
  const videosList = (data.videos && data.videos.length)
    ? data.videos
    : [{ title: data.video_title, url: data.video_url, thumbnail: data.video_thumbnail }];
  const isPlaylist = videosList.length > 1;
  const current = videosList[Math.min(currentIdx, videosList.length - 1)];
  // Le sélecteur de langue (vidéos alternatives) ne concerne que les partages mono-vidéo.
  const videoUrl = isPlaylist ? current.url : (currentVideoUrl || data.video_url);
  const embedUrl = isYouTubeUrl(videoUrl) ? getYouTubeEmbedUrl(videoUrl) : null;
  const isUploaded = isUploadedVideo(videoUrl);
  const hasAlternatives = !isPlaylist && data.language_alternatives && data.language_alternatives.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100" data-testid="shared-video-page">
      <header className="bg-white border-b border-slate-200/60">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm">
            <Film className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-base font-bold text-slate-900 font-['Outfit']">
              {videosList.length > 1 ? 'Vidéos' : 'Vidéo'} d'accompagnement
            </span>
            <p className="text-[11px] text-slate-400 leading-tight">De la part de {data?.doctor_name || 'votre médecin'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Language selector */}
        {hasAlternatives && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="language-selector">
            <Globe className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 mr-1">Langue :</span>
            <button
              onClick={() => setCurrentVideoUrl(data.video_url)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                videoUrl === data.video_url
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300'
              }`}
            >
              {LANG_LABELS[data.language] || data.language || 'Francais'}
            </button>
            {data.language_alternatives.map(alt => (
              <button
                key={alt.language}
                onClick={() => switchLanguage(alt)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  videoUrl === alt.url
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300'
                }`}
              >
                {LANG_LABELS[alt.language] || alt.language}
              </button>
            ))}
          </div>
        )}

        {/* Video Player */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200/60 mb-6">
          <div className="relative bg-black aspect-video">
            {embedUrl && (
              <iframe
                src={embedUrl}
                title={data.video_title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-player-iframe"
              />
            )}
            {isUploaded && (
              <video
                src={videoUrl}
                controls
                className="absolute inset-0 w-full h-full"
                poster={data.video_thumbnail || undefined}
                data-testid="video-player-native"
              >
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>
            )}
            {!embedUrl && !isUploaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <Play className="w-12 h-12 mb-3 opacity-80" />
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-colors backdrop-blur-sm"
                  data-testid="video-external-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir la video
                </a>
              </div>
            )}
          </div>

          <div className="p-6">
            <h1 className="text-lg font-bold text-slate-900 mb-2 font-['Outfit']" data-testid="video-title">
              {isPlaylist ? current.title : data.video_title}
            </h1>
            {isPlaylist && (
              <p className="text-[11px] text-slate-400 -mt-1 mb-1">
                Vidéo {Math.min(currentIdx, videosList.length - 1) + 1} sur {videosList.length}
              </p>
            )}

            {data.doctor_message && (
              <div className="mt-4 p-4 rounded-2xl bg-indigo-50/50 border border-indigo-100">
                <p className="text-[11px] text-indigo-500 font-semibold uppercase tracking-wider mb-2">
                  Message de {data.doctor_name || 'votre médecin'}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="doctor-message">
                  {data.doctor_message}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Playlist (« ordonnance vidéo ») */}
        {isPlaylist && (
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200/60 mb-6 p-4" data-testid="playlist-section">
            <p className="text-[11px] text-indigo-500 font-semibold uppercase tracking-wider mb-3 px-2">
              Les vidéos recommandées pour vous
            </p>
            <div className="space-y-1.5">
              {videosList.map((v, i) => {
                const active = i === Math.min(currentIdx, videosList.length - 1);
                const seen = watched.has(i);
                return (
                  <button
                    key={v.url || i}
                    onClick={() => {
                      setCurrentIdx(i);
                      setWatched(prev => new Set(prev).add(i));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-2xl text-left transition-colors ${
                      active ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`playlist-item-${i}`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                      active ? 'bg-indigo-500 text-white' : seen ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {seen && !active ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    {v.thumbnail && (
                      <img src={v.thumbnail} alt="" loading="lazy" className="w-16 h-10 rounded-lg object-cover bg-slate-100 flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <span className={`text-sm leading-snug line-clamp-2 ${active ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                      {v.title}
                    </span>
                    {active && <Play className="w-4 h-4 text-indigo-400 ml-auto flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Rating section */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-slate-200/60 mb-6 p-6" data-testid="rating-section">
          {ratingSubmitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="text-base font-semibold text-slate-900 mb-1">Merci pour votre évaluation !</p>
              <div className="flex items-center justify-center gap-0.5 mt-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className={`w-5 h-5 ${rating >= i ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                ))}
              </div>
              {comment && <p className="text-sm text-slate-500 mt-2 italic">"{comment}"</p>}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-base font-semibold text-slate-900 mb-1">{videosList.length > 1 ? 'Ces vidéos vous ont-elles été utiles ?' : 'Cette vidéo vous a-t-elle été utile ?'}</p>
              <p className="text-xs text-slate-400 mb-4">Votre avis nous aide à améliorer nos recommandations</p>
              <div className="flex justify-center mb-4">
                <StarRating rating={rating} onRate={setRating} disabled={false} />
              </div>
              {rating > 0 && (
                <div className="max-w-sm mx-auto space-y-3">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Un commentaire pour votre equipe ? (optionnel)"
                    rows={2}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                    data-testid="rating-comment"
                  />
                  <button
                    onClick={handleSubmitRating}
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    data-testid="submit-rating-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Envoyer mon avis
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expiration notice */}
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mb-4">
          <Clock className="w-3.5 h-3.5" />
          <span>Ce lien expire 24 h après sa création</span>
        </div>

        <div className="text-center">
          <p className="text-[10px] text-slate-400 leading-relaxed">
            {videosList.length > 1 ? 'Vidéos partagées' : 'Vidéo partagée'} par votre médecin
          </p>
        </div>
      </main>
    </div>
  );
}
